import { useState } from 'react';
import { Check, Calendar as CalendarIcon, User, Mail, Phone } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { INPUT_CLS, SELECT_CONTENT_CLS, SELECT_ITEM_CLS, LABEL_CLS } from '@/components/brand/BrandKit';
import { TIME_SLOTS, formatDate } from '@/components/booking/bookingData';
import { FleetGallery } from '@/components/booking/FleetGallery';
import { resolveFleetImages } from '@/data/fleetImages';
import { money } from '@/data/transportPricing';
import { bt, vehClass, badgeT, paxLabel, seatLabel } from '@/i18n/bookingI18n';

export function Field({ label, error, children }) {
  return (
    <div>
      <Label className={LABEL_CLS}>{label}</Label>
      {children}
      {error && <p className="text-red-600 text-xs mt-1 font-body">{error}</p>}
    </div>
  );
}

export function DropSelect({ testid, value, onChange, placeholder, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={INPUT_CLS}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className={`${SELECT_CONTENT_CLS} max-h-72`}>
        {options.map((o) => <SelectItem key={o.value} value={o.value} className={SELECT_ITEM_CLS}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function ChoiceTabs({ options, value, onChange, testidPrefix, stacked = false }) {
  const cols = options.length === 3 ? 'grid-cols-3' : 'grid-cols-2';
  return (
    <div className={`grid ${cols} gap-3`}>
      {options.map((o) => (
        <button key={o.id} type="button" data-testid={`${testidPrefix}-${o.id}`} onClick={() => onChange(o.id)}
          className={`flex ${stacked ? 'flex-col gap-1.5 px-2 text-xs sm:text-sm' : 'gap-2 px-4 text-sm'} items-center justify-center rounded-xl border py-3 font-heading font-bold transition-all ${value === o.id ? 'border-[#EE5A01] bg-[#FFF4ED] text-[#EE5A01]' : 'border-black/10 bg-white text-[#121212] hover:border-[#EE5A01]/40'}`}>
          <o.icon className="w-4 h-4" />{o.label}
        </button>
      ))}
    </div>
  );
}

export function PickerBlock({ label, error, children }) {
  return (
    <div>
      <p className="font-heading font-bold text-[11px] tracking-[0.12em] text-[#121212] uppercase mb-3">{label}</p>
      {children}
      {error && <p className="text-red-600 text-xs mt-2 font-body">{error}</p>}
    </div>
  );
}

export function DetailsHeading({ lang = 'en' }) {
  return (
    <>
      <div className="orange-accent-line" />
      <h3 className="font-heading font-bold text-base text-[#121212] uppercase tracking-wider">{bt(lang, 'yourDetails')}</h3>
    </>
  );
}


export function DateTimeRow({ date, setDate, time, setTime, errors, lang = 'en' }) {
  const [open, setOpen] = useState(false);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <Field label={bt(lang, 'pickupDate')} error={errors.date}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" data-testid="mt-date-trigger" className={`w-full flex items-center gap-3 rounded-lg bg-white border h-12 px-3 text-sm transition-colors ${errors.date ? 'border-red-400' : 'border-black/15 hover:border-[#555]'} ${date ? 'text-[#121212]' : 'text-[#666666]'}`}>
              <CalendarIcon className="w-4 h-4 text-[#EE5A01] flex-shrink-0" />{date ? formatDate(date, lang) : bt(lang, 'selectDate')}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-white border-black/15" align="start">
            <Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); setOpen(false); }} disabled={(d) => d < tomorrow} />
          </PopoverContent>
        </Popover>
      </Field>
      <Field label={bt(lang, 'pickupTime')} error={errors.time}>
        <DropSelect testid="mt-time-trigger" value={time} onChange={setTime} placeholder={bt(lang, 'selectTime')} options={TIME_SLOTS.map((t) => ({ value: t.value, label: t.label }))} />
      </Field>
    </div>
  );
}

export function ContactFields({ form, set, errors, lang = 'en' }) {
  const isAr = lang === 'ar';
  const icon = `absolute ${isAr ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-4 h-4 text-[#EE5A01]`;
  const pad = isAr ? 'pr-10' : 'pl-10';
  return (
    <div className="space-y-5">
      <Field label={bt(lang, 'fullName')} error={errors.name}>
        <div className="relative"><User className={icon} /><Input data-testid="mt-name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder={bt(lang, 'namePh')} className={`${INPUT_CLS} ${pad}`} /></div>
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label={bt(lang, 'email')} error={errors.email}>
          <div className="relative"><Mail className={icon} /><Input data-testid="mt-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder={bt(lang, 'emailPh')} className={`${INPUT_CLS} ${pad}`} /></div>
        </Field>
        <Field label={bt(lang, 'mobile971')} error={errors.phone}>
          <div className="relative"><Phone className={icon} /><Input data-testid="mt-phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder={bt(lang, 'phonePh')} className={`${INPUT_CLS} ${pad}`} /></div>
        </Field>
      </div>
      <Field label={bt(lang, 'specialRequests')}>
        <Textarea data-testid="mt-notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder={bt(lang, 'notesPhManaged')} className={`${INPUT_CLS} !h-auto min-h-[80px]`} />
      </Field>
    </div>
  );
}

export function ReturnToggle({ value, onChange, lang = 'en' }) {
  return (
    <button type="button" data-testid="mt-return-toggle" onClick={() => onChange(!value)}
      className={`w-full flex items-center justify-between rounded-xl border px-4 py-3 transition-all ${value ? 'border-[#EE5A01] bg-[#FFF4ED]' : 'border-black/10 bg-white hover:border-[#EE5A01]/40'}`}>
      <span className="font-heading font-bold text-sm text-[#121212]">{bt(lang, 'returnTrip')}</span>
      <span className={`grid h-6 w-6 place-items-center rounded-md ${value ? 'bg-[#EE5A01] text-white' : 'bg-[#F5F5F3] text-transparent'}`}><Check className="w-4 h-4" /></span>
    </button>
  );
}

export function VehiclePicker({ vehicles, value, onChange, priceFor, lang = 'en' }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {vehicles.map((v) => {
        const price = priceFor(v);
        const active = value === v.id;
        const photos = resolveFleetImages(v.name === 'Executive Sedan' ? v.models : v.name, null);
        return (
          <div key={v.id} role="button" tabIndex={0} data-testid={`mt-vehicle-${v.id}`} onClick={() => onChange(v.id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(v.id); } }}
            className={`text-left rounded-xl border p-4 transition-all cursor-pointer ${active ? 'border-[#EE5A01] bg-[#FFF4ED]' : 'border-black/10 bg-white hover:border-[#EE5A01]/40'}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h4 className="font-heading font-bold text-sm text-[#121212]">{vehClass(lang, v.name)}</h4>
                <p className="font-body text-xs text-[#666666] mt-0.5">{seatLabel(lang, v.models)}</p>
              </div>
              {v.badge && <span className="chip-orange !text-[9px] !px-2 !py-0.5">{badgeT(lang, v.badge)}</span>}
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span className="font-body text-[11px] text-[#777]">{paxLabel(lang, v.pax)}</span>
              {price != null && <span className="font-heading font-black text-sm text-[#EE5A01]">{money(price)}</span>}
            </div>
            {photos.length > 0 && (
              <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                <FleetGallery images={photos} name={`${v.name} — ${v.models}`} testid={`mt-vehicle-photos-${v.id}`} triggerClass="text-[#EE5A01] hover:underline" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function EstimateCard({ lines, total, note, error, submitting, onSubmit, disabled, lang = 'en' }) {
  return (
    <div data-testid="mt-estimate" className="bg-white border border-black/10 rounded-2xl p-5 sticky top-28 shadow-sm">
      <h3 className="font-heading font-bold text-xs text-[#EE5A01] uppercase tracking-[0.2em] mb-5">{bt(lang, 'fareEstimate')}</h3>
      {lines.length === 0 ? (
        <p className="font-body text-sm text-[#666666]">{bt(lang, 'estimateEmpty')}</p>
      ) : (
        <div className="space-y-2 text-sm">
          {lines.map((l) => (
            <div key={l.label} className="flex justify-between gap-3">
              <span className="font-body text-[#666666]">{l.label}</span>
              <span className={`font-heading text-right ${l.muted ? 'text-[#777]' : 'text-[#121212]'}`}>{l.value}</span>
            </div>
          ))}
          <div className="flex justify-between items-center pt-3 mt-1 border-t border-black/10">
            <span className="font-heading font-bold text-[#121212] uppercase text-xs tracking-wider">{bt(lang, 'estimatedTotal')}</span>
            <span data-testid="mt-total" className="font-heading font-black text-xl text-[#EE5A01]">{money(total)}</span>
          </div>
        </div>
      )}
      {note && <p className="mt-4 font-body text-[11px] leading-relaxed text-[#888]">{note}</p>}
      {error && <p data-testid="mt-submit-error" role="alert" className="mt-4 bg-red-50 border border-red-200 text-red-600 font-body text-sm p-3 rounded-lg">{error}</p>}
      <button type="button" data-testid="mt-submit" onClick={onSubmit} disabled={disabled || submitting}
        className="btn-primary w-full mt-5 disabled:opacity-50">{submitting ? bt(lang, 'submitting') : bt(lang, 'requestBooking')}</button>
      <p className="mt-3 font-body text-[10px] text-center text-[#999]">{bt(lang, 'estimateFooter')}</p>
    </div>
  );
}

export function ManagedConfirmation({ name, bookingRef, rows, onReset, lang = 'en' }) {
  return (
    <div data-testid="mt-confirmation" className="bg-white border border-black/10 rounded-2xl p-6 sm:p-8 shadow-sm max-w-lg mx-auto text-center">
      <div className="w-16 h-16 rounded-full bg-[#EE5A01] flex items-center justify-center mx-auto mb-5"><Check className="w-8 h-8 text-white" /></div>
      <h2 className="font-heading font-black text-2xl sm:text-3xl text-[#121212] tracking-tight">{bt(lang, 'bookingReceived')}</h2>
      <p className="mt-3 font-body text-[#666666]">{bt(lang, 'thankYouManaged', name)}</p>
      <div className="mt-6 text-left space-y-3 border-t border-black/10 pt-5">
        <div className="flex justify-between"><span className="font-body text-xs uppercase text-[#666666]">{bt(lang, 'reference')}</span><span data-testid="mt-confirmation-ref" className="font-mono text-sm text-[#EE5A01]">{bookingRef}</span></div>
        {rows.map((r) => <div key={r.label} className="flex justify-between gap-3"><span className="font-body text-xs uppercase text-[#666666]">{r.label}</span><span className="font-heading text-sm text-[#121212] text-right">{r.value}</span></div>)}
      </div>
      <button type="button" data-testid="mt-book-another" onClick={onReset} className="btn-outline mt-6">{bt(lang, 'bookAnother')}</button>
    </div>
  );
}
